import {useInfiniteQuery} from '@tanstack/react-query';
import {useState} from 'react';
import {useParams} from 'react-router-dom';
import {DataTableSortStatus} from 'mantine-datatable';
// The comparator is SHARED (FNP-912): nulls-last and numeric-vs-string live in the
// library, not in a per-module copy.
import {sortRecords} from '@flexinet/ui-components';

import {fetchRecords} from '../services/PaymentEventsService';
import {PaymentEvent} from '../types';

// Page size is a server concern fixed by the module (§9), never a user choice.
const PAGE_SIZE = 10;

// NEWEST RECORDED FIRST, on `created`: the list index's sort key
// (record-type-created-index, ScanIndexForward false), so first paint agrees with
// the server's own order. `occurredAt` is Stripe's event time, nullable and not
// indexed, so it is a sortable column but not the default.
//
// The billing-api resolver drops the `sort` argument (list.ts passes only filter,
// limit and nextToken), so ordering is applied here rather than requested.
export const DEFAULT_SORT_STATUS: DataTableSortStatus<PaymentEvent> = {
    columnAccessor: 'created',
    direction: 'desc',
};

const matchesKeyword = (item: PaymentEvent, needle: string): boolean => {
    if (!needle) {
        return true;
    }
    const haystack = [
        item.status,
        item.currency,
        item.paymentMethodLast4,
        item.failureCode,
        item.failureMessage,
        item.id,
    ];
    return haystack.some(value => typeof value === 'string' && value.toLowerCase()
                                                                    .includes(needle));
};

export const paymentEventsQueryKeys = {
    all: ['payment-events'] as const,
    list: (invoiceId?: string) => ['payment-events', 'list', {invoiceId}] as const,
};

export const usePaymentEvents = (invoiceIdProp?: string) => {
    const params = useParams();
    const invoiceId = invoiceIdProp ?? params.invoiceId ?? params.id;

    // Component state, not a module-scoped store. The search input (TableSearchForm)
    // and this hook are used by the SAME component, RecordsTable, so there is no
    // sibling for a store to reach (the FNP-815 failure needs two components). A
    // module-wide keyword would also carry one invoice's search into the next
    // invoice's tab, because this module renders inside each invoice's page.
    const [keyword, setKeyword] = useState('');

    // Sort is how one operator is viewing the table right now: component state.
    const [sortStatus, setSortStatus] =
        useState<DataTableSortStatus<PaymentEvent>>(DEFAULT_SORT_STATUS);

    // Only what the server filters on is in the key. The keyword is applied
    // client-side below, so keying on it would refetch identical pages per keystroke.
    const query = useInfiniteQuery({
        queryKey: paymentEventsQueryKeys.list(invoiceId),
        queryFn: ({pageParam}: { pageParam: string | null }) => fetchRecords({
            limit: PAGE_SIZE,
            nextToken: pageParam,
            filter: invoiceId ? {invoiceId} : undefined,
        }),
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) return undefined;
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null,
        enabled: !!invoiceId,
        // Rows are written by the Stripe webhook, not by this UI; 30s stops a tab
        // switch from refetching while Reload stays the way to see a new event.
        staleTime: 30_000,
    });

    const items: PaymentEvent[] =
        query.data?.pages?.flatMap(page => (page && 'items' in page && page.items) ? page.items : []) ?? [];

    const needle = keyword.trim()
                          .toLowerCase();

    // ⚠️ Client-side filter and sort cover the pages FETCHED SO FAR. Under token
    // pagination the browser cannot order or search rows it has not loaded; that is
    // a property of the model, not a bug (§9).
    const visibleItems = sortRecords(
        items.filter(item => matchesKeyword(item, needle)),
        sortStatus
    );

    const lastPage = query.data?.pages?.[query.data.pages.length - 1];

    return {
        // The loaded set BEFORE keyword filtering: what the footer's
        // "N of M loaded" is measured against.
        items,
        visibleItems,
        keyword,
        sortStatus,
        setSortStatus,
        loading: query.isLoading,
        isError: query.isError,
        isFetchingMore: query.isFetchingNextPage,
        // The server's own cursor, never a stand-in: opaque to this client (§9).
        nextToken: query.hasNextPage ? (lastPage?.nextToken ?? null) : null,
        fetchRecords: () => {
            if (query.hasNextPage && !query.isFetchingNextPage) {
                query.fetchNextPage();
            }
        },
        handleFilterByKeyword: (newKeyword: string) => setKeyword(newKeyword),
    };
};
